import { CHAT_COLUMN_MAX_WIDTH } from '@fruitful/agent-ui';
import type { ThreadMetricsSurfaceState } from '@fruitful/llm-service-shared';
import { CMMBranch, OnboardingChatModule } from '@fruitful/types';
import React, { useRef } from 'react';
import { View, XStack } from 'smoothie';

const SYSTEM_MODULE_TO_OVAL: Record<string, number> = {
    [OnboardingChatModule.Debt]: 0,
    [OnboardingChatModule.DebtCheck]: 0,
    [OnboardingChatModule.Income]: 1,
    [OnboardingChatModule.PreTaxIncome]: 1,
    [OnboardingChatModule.CoreNumbers]: 2,
    [OnboardingChatModule.Savings]: 3,
};

const SYSTEM_TOTAL_STEPS =
    Math.max(...Object.values(SYSTEM_MODULE_TO_OVAL)) + 1;

interface BranchConfig {
    moduleToOval: Record<string, number>;
    totalSteps: number;
}

const BRANCH_CONFIG: Partial<Record<CMMBranch, BranchConfig>> = {
    [CMMBranch.GoalsSystem]: {
        moduleToOval: SYSTEM_MODULE_TO_OVAL,
        totalSteps: SYSTEM_TOTAL_STEPS,
    },
};

interface CMMChatProgressState {
    completedSteps: number;
    totalSteps: number;
}

const getCMMChatProgress = (
    threadMetadata: ThreadMetricsSurfaceState | null,
): CMMChatProgressState | null => {
    if (threadMetadata?.branch == null) {
        return null;
    }

    const config = BRANCH_CONFIG[threadMetadata.branch];
    if (config == null) {
        return null;
    }

    const { collectedModules } = threadMetadata;
    if (collectedModules.length === 0) {
        return { completedSteps: 0, totalSteps: config.totalSteps };
    }

    let maxOval = -1;
    for (const mod of collectedModules) {
        const oval = config.moduleToOval[mod];
        if (oval != null && oval > maxOval) {
            maxOval = oval;
        }
    }

    return {
        completedSteps: maxOval + 1,
        totalSteps: config.totalSteps,
    };
};

interface OnboardingChatProgressBarProps {
    threadMetadata: ThreadMetricsSurfaceState | null;
    userMessageCount?: number;
}

export const OnboardingChatProgressBar = ({
    threadMetadata,
    userMessageCount = 0,
}: OnboardingChatProgressBarProps) => {
    const chatProgress = getCMMChatProgress(threadMetadata);
    const isVisible = chatProgress != null;

    const msgCountAtStepChangeRef = useRef(userMessageCount);
    const prevCompletedStepsRef = useRef<number | null>(null);
    const wasVisibleRef = useRef(isVisible);

    const currentSteps = chatProgress?.completedSteps ?? 0;
    const becameVisible = isVisible && !wasVisibleRef.current;
    if (becameVisible || currentSteps !== prevCompletedStepsRef.current) {
        prevCompletedStepsRef.current = currentSteps;
        msgCountAtStepChangeRef.current = userMessageCount;
    }
    wasVisibleRef.current = isVisible;

    const userRespondedInSection =
        isVisible && userMessageCount > msgCountAtStepChangeRef.current;

    return (
        <View
            width="100%"
            alignItems="center"
            overflow="hidden"
            style={{
                maxHeight: isVisible ? 42 : 0,
                opacity: isVisible ? 1 : 0,
                transition: 'max-height 350ms ease, opacity 300ms ease 50ms',
            }}
        >
            {chatProgress != null && (
                <XStack
                    width="100%"
                    maxWidth={CHAT_COLUMN_MAX_WIDTH}
                    gap={4}
                    alignItems="center"
                    justifyContent="center"
                    paddingVertical={18}
                >
                    {Array.from({ length: chatProgress.totalSteps }, (_, i) => {
                        const isCompleted = i < chatProgress.completedSteps;
                        const isCurrent = i === chatProgress.completedSteps;

                        return (
                            <View
                                key={i}
                                width={26}
                                height={6}
                                borderRadius={3}
                                backgroundColor="rgba(120, 120, 120, 0.2)"
                                overflow="hidden"
                            >
                                <View
                                    height="100%"
                                    borderRadius={3}
                                    backgroundColor="$fruitfulGreen"
                                    width={
                                        isCompleted
                                            ? '100%'
                                            : isCurrent
                                              ? userRespondedInSection
                                                  ? '50%'
                                                  : '20%'
                                              : '0%'
                                    }
                                    style={{
                                        transition: isCurrent
                                            ? 'width 300ms ease 400ms'
                                            : 'width 400ms ease',
                                    }}
                                />
                            </View>
                        );
                    })}
                </XStack>
            )}
        </View>
    );
};
